import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Check, Pencil, ImageUp, Trash, Plus, Camera } from "lucide-react";
import { CameraModal } from "@/components/CameraModal";
import { fileToFileList } from "@/utils/fileList";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { updateClientsData, getShowClients } from "@/Api/clients";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { getSelectClientOrigins } from "@/Api/clientOrigins";
import { getSelectDiscountTypes } from "@/Api/discountTypes";
import { getSelectSellers } from "@/Api/sellers";
import { SellersField, LinkedSeller } from "../SellersField";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FormSteps, useStepControl } from "@/components/Form/Steps";
import { AddressSearch } from "@/components/AddressSearch";
import { GoogleMap, loadGoogleMapsScript } from "@/components/GoogleMap";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { GENDERS } from "@/constants/gender";
import { Textarea } from "@/components/ui/textarea";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useCanEditClientDiscount } from "@/hooks/auth";
import { Switch } from "@/components/ui/switch";

interface SelectOption {
  id: string;
  name: string;
  user: {
    name: string;
  };
}

const step1SchemaBase = z.object({
  name: z.string().min(1, "*requerido").max(255),
  email: z.any(),
  document: z.any(),
  phone: z.string().optional(),
  phone_2: z.string().optional(),
  image: z.any(),
  gender_id: z.string().min(1, "*requerido"),
  birth_date: z.any(),
  discount: z.string().optional(),
  discount_type_id: z.string().optional(),
  client_origin_id: z.string().min(1, "*requerido"),
  client_origin_photo: z.any(),
  seller_ids: z.array(z.string()).optional(),
  has_purchase_voucher: z.boolean().optional(),
  max_purchase_voucher_value: z.string().optional(),
});

const step1Schema = step1SchemaBase.refine(
  (data) => {
    return (data.phone && data.phone.length > 0) || (data.phone_2 && data.phone_2.length > 0);
  },
  {
    message: "Pelo menos um telefone é obrigatório",
    path: ["phone"],
  }
);

const addressSchema = z.object({
  id: z.number().optional(),
  street: z.any(),
  number: z.any(),
  neighborhood: z.any(),
  complement: z.string().max(255).optional(),
  state: z.any(),
  city: z.any(),
  zip_code: z.any(),
  is_main: z.any(),
  reference_point: z.string().max(255).optional(),
  latitude: z.string().optional(),
  longitude: z.string().optional(),
  addressName: z.string().optional(),
});

const step2Schema = z.object({
  addresses: z.array(addressSchema).min(1, "Adicione pelo menos um endereço"),
  observation: z.string().max(500).optional(),
});

const schemas = {
  1: step1Schema,
  2: step2Schema,
};

const schema = z.object({
  ...step1SchemaBase.shape,
  ...step2Schema.shape,
}).refine(
  (data) => {
    return (data.phone && data.phone.length > 0) || (data.phone_2 && data.phone_2.length > 0);
  },
  {
    message: "Pelo menos um telefone é obrigatório",
    path: ["phone"],
  }
);

type SchemaType = z.infer<typeof schema>;

export const UpdateBtn = ({
  updateFront,
  uuid,
}: {
  updateFront: (response: any) => void;
  uuid: string;
}) => {
  const [clientOrigins, setClientOrigins] = useState<SelectOption[]>([]);
  const [discountTypes, setDiscountTypes] = useState<SelectOption[]>([]);
  const [sellers, setSellers] = useState<SelectOption[]>([]);
  const [linkedSellers, setLinkedSellers] = useState<LinkedSeller[]>([]);
  const stepControls = useStepControl();
  const [isOpen, setIsOpen] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [originPhotoPreview, setOriginPhotoPreview] = useState<string | null>(null);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const isAdminView = useCanEditClientDiscount();

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      gender_id: "",
      email: "",
      document: "",
      phone: "",
      phone_2: "",
      birth_date: "",
      discount: "",
      discount_type_id: "1",
      client_origin_id: "",
      client_origin_photo: null,
      seller_ids: [],
      has_purchase_voucher: false,
      max_purchase_voucher_value: "",
      addresses: [
        {
          street: "",
          number: "",
          neighborhood: "",
          complement: "",
          state: "",
          city: "",
          zip_code: "",
          is_main: 1,
          reference_point: "",
          latitude: "",
          longitude: "",
          addressName: "",
        },
      ],
      observation: "",
      image: null,
    },
  });

  const { toast } = useToastNavigate();
  const [formState, setFormState] = useState<string>("default");
  const formWatch = form.watch();

  useEffect(() => {
    if (!isOpen) return;

    const loadGoogleMaps = async () => {
      try {
        await loadGoogleMapsScript();
        setIsGoogleMapsLoaded(true);
      } catch (error) {
        console.error("Error loading Google Maps:", error);
        setIsGoogleMapsLoaded(false);
      }
    };
    loadGoogleMaps();

    const loadData = async () => {
      setIsLoading(true);
      try {
        const [
          clientRes,
          originsRes,
          discountTypesRes,
          sellersRes,
        ] = await Promise.all([
          getShowClients(uuid),
          getSelectClientOrigins(),
          getSelectDiscountTypes(),
          getSelectSellers({ is_active: true }),
        ]);

        if (clientRes.ok) {
          const data = clientRes.data?.response;
          form.setValue("name", data?.name || "");
          form.setValue("email", data?.email || "");
          form.setValue("document", data?.document || "");
          const formatPhone = (phone: string | null | undefined) => {
            if (!phone) return "";
            return phone.startsWith("+") ? phone : `+${phone}`;
          };
          form.setValue("phone", formatPhone(data?.phone));
          form.setValue("phone_2", formatPhone(data?.phone_2));
          form.setValue("birth_date", data?.birth_date || "");
          form.setValue("gender_id", data?.gender_id?.toString() || "");
          form.setValue("discount", data?.discount?.toString() || "");
          form.setValue(
            "discount_type_id",
            data?.discount_type_id?.toString() || ""
          );
          form.setValue(
            "client_origin_id",
            data?.client_origin_id?.toString() || ""
          );
          setLinkedSellers(
            (data?.sellers?.length ? data.sellers : data?.seller ? [data.seller] : []).map(
              (seller: any) => ({
                id: seller.id,
                name: seller?.user?.name || String(seller.id),
              })
            )
          );
          form.setValue("has_purchase_voucher", data?.has_purchase_voucher || false);
          form.setValue("max_purchase_voucher_value", data?.max_purchase_voucher_value?.toString() || "");
          form.setValue("observation", data?.observation ?? "");

          // Carregar endereços
          if (data?.addresses && data?.addresses.length > 0) {
            const formattedAddresses = data.addresses.map((addr: any) => ({
              id: addr.id,
              street: addr.street || "",
              number: addr.number || "",
              neighborhood: addr.neighborhood || "",
              complement: addr.complement || "",
              state: addr.state || "",
              city: addr.city || "",
              zip_code: addr.zip_code || "",
              is_main: addr.pivot?.is_main || 0,
              reference_point: addr.reference_point || "",
              latitude: addr.latitude?.toString() || "",
              longitude: addr.longitude?.toString() || "",
              addressName: addr.street ? `${addr.street}, ${addr.number || ''} - ${addr.neighborhood || ''}, ${addr.city || ''} - ${addr.state || ''}` : "",
            }));
            form.setValue("addresses", formattedAddresses);
          } else if (data?.address) {
            // Para compatibilidade com versões anteriores
            form.setValue("addresses", [
              {
                id: data.address.id,
                street: data.address.street || "",
                number: data.address.number || "",
                neighborhood: data.address.neighborhood || "",
                complement: data.address.complement || "",
                state: data.address.state || "",
                city: data.address.city || "",
                zip_code: data.address.zip_code || "",
                is_main: 1,
                reference_point: data.address.reference_point || "",
                latitude: data.address.latitude?.toString() || "",
                longitude: data.address.longitude?.toString() || "",
                addressName: data.address.street ? `${data.address.street}, ${data.address.number || ''} - ${data.address.neighborhood || ''}, ${data.address.city || ''} - ${data.address.state || ''}` : "",
              },
            ]);
          }

          setOriginPhotoPreview(data?.client_origin_photo || null);
        }

        if (originsRes.ok) setClientOrigins(originsRes.data?.response || []);
        if (discountTypesRes.ok)
          setDiscountTypes(discountTypesRes.data?.response || []);
        if (sellersRes.ok) setSellers(sellersRes.data?.response || []);
      } catch (error) {
        console.error("Error loading data:", error);
        toast({
          description: "Erro ao carregar dados",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [isOpen, uuid]);

  const handleNextStep = async () => {
    const currentSchema = schemas[stepControls.step as keyof typeof schemas];
    const currentStepData = form.getValues();

    try {
      await currentSchema.parseAsync(currentStepData);


      stepControls.setStep(stepControls.step + 1);
    } catch (error) {
      form.trigger();
    }
  };

  const formSubmit = async (data: SchemaType) => {
    setFormState("loading");

    const payload = {
      ...data,
      image: data?.image?.[0] || null,
      client_origin_photo: data?.client_origin_photo?.[0] || null,
      discount_type_id: data?.discount_type_id || "1",
      discount: data?.discount || 0,
      has_purchase_voucher: data?.has_purchase_voucher ? 1 : 0,
      max_purchase_voucher_value: data?.has_purchase_voucher && data?.max_purchase_voucher_value
        ? parseFloat(data.max_purchase_voucher_value)
        : null,
    };

    const { data: apiResponse, ok } = await updateClientsData(uuid, payload);
    if (!ok) {
      setFormState("default");
      const errorMessage = apiResponse?.response || "Erro ao atualizar cliente";

      if (errorMessage.toLowerCase().includes("telefone")) {
        if (errorMessage.toLowerCase().includes("telefone 2")) {
          form.setError("phone_2", { type: "manual", message: errorMessage });
        } else {
          form.setError("phone", { type: "manual", message: errorMessage });
        }
        stepControls.setStep(1);
      } else {
        toast({
          description: errorMessage,
          variant: "destructive",
        });
      }
      return;
    }

    updateFront(apiResponse?.response);

    stepControls.setStep(1);
    setFormState("default");
    setIsOpen(false);
    toast({
      description: `Cliente atualizado com sucesso!`,
      action: <Check color="#1edc2b" />,
    });
  };

  const addNewAddress = () => {
    const addresses = form.getValues("addresses");
    form.setValue("addresses", [
      ...addresses,
      {
        street: "",
        number: "",
        neighborhood: "",
        complement: "",
        state: "",
        city: "",
        zip_code: "",
        is_main: 0,
        reference_point: "",
        latitude: "",
        longitude: "",
        addressName: "",
      },
    ]);
  };

  const handleAddressSelect = (
    index: number,
    selectedAddress: {
      addressName: string;
      street: string;
      number: string;
      neighborhood: string;
      city: string;
      state: string;
      zip_code: string;
      latitude: string;
      longitude: string;
    }
  ) => {
    const addresses = form.getValues("addresses");
    addresses[index] = {
      ...addresses[index],
      ...selectedAddress,
    };
    form.setValue("addresses", addresses);
  };

  const setMainAddress = (index: number) => {
    const addresses = form.getValues("addresses").map((addr, i) => ({
      ...addr,
      is_main: i === index ? 1 : 0,
    }));
    form.setValue("addresses", addresses);
  };

  const removeAddress = (index: number) => {
    const addresses = form.getValues("addresses");
    if (addresses.length > 1) {
      const newAddresses = addresses.filter((_, i) => i !== index);
      // Se o endereço removido era o principal, torna o primeiro como principal
      if (addresses[index].is_main === 1 && newAddresses.length > 0) {
        newAddresses[0].is_main = 1;
      }
      form.setValue("addresses", newAddresses);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
        >
          <Pencil className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[650px] max-h-[90vh]">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Atualizar Cliente
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center items-center h-[400px]">
            <LoaderSpin />
          </div>
        ) : (
          <FormSteps controls={stepControls}>
            <FormSteps.Header>
              <FormSteps.Indicator step={1}>
                Informações do Cliente
              </FormSteps.Indicator>
              <FormSteps.Indicator step={2}>Endereços</FormSteps.Indicator>
            </FormSteps.Header>

            <ScrollArea className="h-[calc(90vh-15rem)] sm:h-[calc(90vh-12rem)] pr-4">
              <Form
                onSubmit={form.handleSubmit(formSubmit)}
                {...form}
                className="space-y-3 p-2"
              >
                <FormSteps.Content step={1}>
                  <Form.Row>
                    <Form.Field name="name">
                      {({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            Nome Completo <Form.Message />
                          </Form.Label>
                          <Form.Control>
                            <Input
                              {...field}
                              placeholder="Nome completo do cliente"
                              onChange={(e) => {
                                const uppercaseValue =
                                  e.target.value.toUpperCase();
                                field.onChange(uppercaseValue);
                              }}
                            />
                          </Form.Control>
                        </Form.Item>
                      )}
                    </Form.Field>
                  </Form.Row>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Telefone <span className="text-red-500">*</span>
                      {form.formState.errors.phone && (
                        <span className="text-red-500 text-xs ml-2">
                          {form.formState.errors.phone.message as string}
                        </span>
                      )}
                    </Label>
                    <Form.Field name="phone">
                      {({ field }) => (
                        <PhoneInput
                          placeholder="+55"
                          value={field.value}
                          onChange={(value) => field.onChange(value || "")}
                          defaultCountry="BR"
                          international
                          countryCallingCodeEditable={false}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        />
                      )}
                    </Form.Field>
                    <Form.Field name="phone_2">
                      {({ field }) => (
                        <PhoneInput
                          placeholder="+55"
                          value={field.value}
                          onChange={(value) => field.onChange(value || "")}
                          defaultCountry="BR"
                          international
                          countryCallingCodeEditable={false}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        />
                      )}
                    </Form.Field>
                  </div>

                  <Form.Row>
                    <Form.Field name="document">
                      {({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            CPF <Form.Message />
                          </Form.Label>
                          <Form.Control>
                            <Input
                              {...field}
                              typeMask="cpfOrCnpj"
                              placeholder="CPF"
                            />
                          </Form.Control>
                        </Form.Item>
                      )}
                    </Form.Field>

                    <Form.Field name="birth_date">
                      {({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            Data de Nascimento <Form.Message />
                          </Form.Label>
                          <Form.Control>
                            <Input
                              {...field}
                              type="date"
                              placeholder="Data de nascimento"
                            />
                          </Form.Control>
                        </Form.Item>
                      )}
                    </Form.Field>
                  </Form.Row>

                  <FormField
                    control={form.control}
                    name="gender_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Gênero*</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o gênero" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {GENDERS.map((gender) => (
                              <SelectItem
                                key={gender.id}
                                value={gender.id.toString()}
                              >
                                {gender.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Form.Row>
                    <Form.Field name="email">
                      {({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            Email <Form.Message />
                          </Form.Label>
                          <Form.Control>
                            <Input
                              {...field}
                              type="email"
                              placeholder="Email"
                            />
                          </Form.Control>
                        </Form.Item>
                      )}
                    </Form.Field>
                  </Form.Row>

                  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-4 space-y-4">
                    <Form.Row>
                      <Form.Field name="client_origin_id">
                        {({ field }) => (
                          <Form.Item>
                            <Form.Label>
                              Origem do Cliente <Form.Message />
                            </Form.Label>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione" />
                              </SelectTrigger>
                              <SelectContent>
                                {clientOrigins.map((origin) => (
                                  <SelectItem
                                    key={origin.id}
                                    value={String(origin.id)}
                                  >
                                    {origin.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </Form.Item>
                        )}
                      </Form.Field>
                    </Form.Row>

                    {formWatch.client_origin_id && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">
                          Foto da Origem <span className="text-red-500">*</span>
                        </Label>
                        <div className="flex justify-center items-center rounded-md border border-dashed dark:border-gray-500 border-gray-900/25 px-2 py-3">
                          <div className="flex flex-col items-center gap-4">
                            {formWatch?.client_origin_photo?.length > 0 || originPhotoPreview ? (
                              <img
                                className="rounded-md aspect-square w-32 object-cover"
                                src={
                                  formWatch?.client_origin_photo?.length > 0
                                    ? URL.createObjectURL(formWatch?.client_origin_photo?.[0])
                                    : originPhotoPreview || ""
                                }
                                alt="origin_photo"
                              />
                            ) : (
                              <ImageUp className="mx-auto size-10 text-gray-300" />
                            )}
                            <div className="flex justify-center items-center gap-5 text-sm text-gray-600">
                              <label
                                htmlFor="origin-photo-upload"
                                className="relative cursor-pointer rounded-md bg-transparent font-semibold text-blue-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 hover:text-blue-700"
                              >
                                <span className="flex items-center gap-1">
                                  {(formWatch?.client_origin_photo?.length > 0 || originPhotoPreview) && (
                                    <ImageUp className="size-4" />
                                  )}
                                  {formWatch?.client_origin_photo?.length > 0 || originPhotoPreview ? "Alterar foto" : "Anexar foto"}
                                </span>
                                <input
                                  id="origin-photo-upload"
                                  type="file"
                                  className="sr-only"
                                  accept="image/*"
                                  {...form.register("client_origin_photo")}
                                />
                              </label>
                              <button
                                type="button"
                                className="flex items-center gap-1 font-semibold text-blue-600 hover:text-blue-700"
                                onClick={() => setIsCameraOpen(true)}
                              >
                                <Camera className="size-4" />
                                Tirar foto
                              </button>
                              {(formWatch?.client_origin_photo?.length > 0 || originPhotoPreview) && (
                                <Button
                                  type="button"
                                  className="h-6 px-0 bg-transparent text-slate font-medium hover:text-red-600 hover:bg-transparent flex gap-1"
                                  onClick={() => {
                                    form.setValue("client_origin_photo", null);
                                    setOriginPhotoPreview(null);
                                  }}
                                >
                                  <Trash className="size-4" />
                                  Remover
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <Form.Row>
                    <Form.Field name="seller_ids">
                      {({ field }) => (
                        <SellersField
                          sellers={sellers}
                          value={field.value || []}
                          onChange={field.onChange}
                          linked={linkedSellers}
                        />
                      )}
                    </Form.Field>
                  </Form.Row>

                  {isAdminView(() => (
                    <Form.Row>
                      <Form.Field name="discount">
                        {({ field }) => (
                          <Form.Item>
                            <Form.Label>
                              Desconto (%) <Form.Message />
                            </Form.Label>
                            <Form.Control>
                              <Input
                                {...field}
                                type="number"
                                placeholder="Desconto"
                              />
                            </Form.Control>
                          </Form.Item>
                        )}
                      </Form.Field>

                      <Form.Field name="discount_type_id">
                        {({ field }) => (
                          <Form.Item>
                            <Form.Label>
                              Tipo de Desconto <Form.Message />
                            </Form.Label>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione" />
                              </SelectTrigger>
                              <SelectContent>
                                {discountTypes.map((type) => (
                                  <SelectItem
                                    key={type.id}
                                    value={String(type.id)}
                                  >
                                    {type.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </Form.Item>
                        )}
                      </Form.Field>
                    </Form.Row>
                  ))}

                  {isAdminView(() => (
                  <div className="rounded-lg border p-4 space-y-4">
                    <Form.Field name="has_purchase_voucher">
                      {({ field }) => (
                        <Form.Item className="flex flex-row items-center justify-between">
                          <div className="space-y-0.5">
                            <Form.Label className="text-base">
                              Aceita Vale Compra
                            </Form.Label>
                            <p className="text-sm text-muted-foreground">
                              Este cliente aceita receber vale compra?
                            </p>
                          </div>
                          <Form.Control>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </Form.Control>
                        </Form.Item>
                      )}
                    </Form.Field>

                    {form.watch('has_purchase_voucher') && (
                      <Form.Field name="max_purchase_voucher_value">
                        {({ field }) => (
                          <Form.Item>
                            <Form.Label>
                              Valor Máximo (R$) <Form.Message />
                            </Form.Label>
                            <Form.Control>
                              <Input
                                {...field}
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="Ex: 500.00"
                              />
                            </Form.Control>
                          </Form.Item>
                        )}
                      </Form.Field>
                    )}
                  </div>
                  ))}

                </FormSteps.Content>

                <FormSteps.Content step={2}>
                  {formWatch.addresses?.map((address, index) => (
                    <div key={index} className="mb-8 relative border rounded-lg p-4">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium">
                          Endereço {index + 1}
                        </h3>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setMainAddress(index)}
                            className={cn(
                              "text-sm",
                              address.is_main === 1 && "bg-primary text-white"
                            )}
                          >
                            {address.is_main === 1
                              ? "Principal"
                              : "Tornar Principal"}
                          </Button>
                          {formWatch.addresses.length > 1 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => removeAddress(index)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="grid gap-2">
                          <Label>Buscar endereço</Label>
                          <AddressSearch
                            onAddressSelect={(selectedAddress) =>
                              handleAddressSelect(index, selectedAddress)
                            }
                            placeholder="Digite o endereço para buscar..."
                            defaultValue={address.addressName || ""}
                          />
                        </div>

                        {address.latitude && address.longitude && (
                          <div className="mt-4">
                            {isGoogleMapsLoaded ? (
                              <GoogleMap
                                latitude={parseFloat(address.latitude)}
                                longitude={parseFloat(address.longitude)}
                                address={address.addressName}
                                className="h-48"
                              />
                            ) : (
                              <div className="w-full h-48 rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center">
                                <span className="text-gray-500">
                                  Carregando Google Maps...
                                </span>
                              </div>
                            )}
                            <div className="text-sm text-gray-600 mt-2 p-2 bg-gray-50 rounded">
                              <span className="font-medium">Endereço selecionado:</span>{" "}
                              {address.addressName}
                            </div>
                          </div>
                        )}

                        <Form.Row>
                          <Form.Field name={`addresses.${index}.complement`}>
                            {({ field }) => (
                              <Form.Item>
                                <Form.Label>
                                  Complemento <Form.Message />
                                </Form.Label>
                                <Form.Control>
                                  <Input {...field} placeholder="Complemento" />
                                </Form.Control>
                              </Form.Item>
                            )}
                          </Form.Field>

                          <Form.Field name={`addresses.${index}.reference_point`}>
                            {({ field }) => (
                              <Form.Item>
                                <Form.Label>
                                  Ponto de Referência <Form.Message />
                                </Form.Label>
                                <Form.Control>
                                  <Input
                                    {...field}
                                    placeholder="Ponto de referência"
                                  />
                                </Form.Control>
                              </Form.Item>
                            )}
                          </Form.Field>
                        </Form.Row>
                      </div>
                    </div>
                  ))}

                  <Button
                    type="button"
                    variant="outline"
                    onClick={addNewAddress}
                    className="w-full mt-4"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Adicionar Novo Endereço
                  </Button>

                  <Form.Row>
                    <Form.Field name="observation">
                      {({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            Observação do Atendente <Form.Message />
                          </Form.Label>
                          <Form.Control>
                            <Textarea
                              {...field}
                              placeholder="Observações do atendente"
                              rows={3}
                            />
                          </Form.Control>
                        </Form.Item>
                      )}
                    </Form.Field>
                  </Form.Row>
                </FormSteps.Content>

                <DialogFooter className="sticky bottom-0 pt-4">
                  <div className="flex w-full gap-2">
                    {stepControls.step > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() =>
                          stepControls.setStep(stepControls.step - 1)
                        }
                      >
                        Voltar
                      </Button>
                    )}

                    {stepControls.step < 2 && (
                      <Button
                        type="button"
                        className={cn("flex-1", colors.bg_primary)}
                        onClick={handleNextStep}
                      >
                        Próximo
                      </Button>
                    )}

                    {stepControls.step === 2 && (
                      <Button
                        type="submit"
                        className={cn("flex-1", colors.bg_primary)}
                        disabled={formState !== "default"}
                      >
                        {formState === "loading" && (
                          <>
                            Atualizando <LoaderSpin />
                          </>
                        )}
                        {formState === "default" && "Atualizar"}
                        {formState === "error" && "Erro ao atualizar"}
                      </Button>
                    )}
                  </div>
                </DialogFooter>
              </Form>
            </ScrollArea>
          </FormSteps>
        )}
      <CameraModal
          isOpen={isCameraOpen}
          mirror
          squareGuide
          onClose={() => setIsCameraOpen(false)}
          onCapture={(file) => {
            form.setValue("client_origin_photo", fileToFileList(file), {
              shouldValidate: true,
            });
            setIsCameraOpen(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
};
